import React, { FC, useEffect, memo } from 'react';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { useOnboardingContext } from 'application/components/common/SelectPlayerFlow/useOnboardingContext';
import { SelectPlayerFlowType } from 'application/components/common/SelectPlayerFlow/types';
import { RouterUtils, useNavigate, useParams } from 'application/providers/RouterProvider';
import { StreamGroupsCollectionPageParamsType } from 'application/pages/types';
import { useNotificationContext } from 'application/providers/NotificationProvider';
import { POINTS_PAGE_PATH } from 'application/consts';

import { PointInfoTypeFragment } from 'domain/api/graphql/generated';

import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';

import usePointCreateViewModel from './usePointCreateModalViewModel';
import { PointCreateModalPages } from './types';
import { PopularTariffWarning, ModalTitle, ModalContent, PointCreateButton } from './components';

import styles from './index.module.scss';

const PointCreateModal: FC<ModalProps[ModalTypes.POINT_CREATE]> = ({ close, replaceModal, openModal }) => {
  const { flowType, isTemplateMode } = useOnboardingContext();
  const pathParams = useParams<StreamGroupsCollectionPageParamsType>();
  const navigate = useNavigate();
  const notification = useNotificationContext();
  const { isIpad } = useMedia();

  const targetStreamId = pathParams.id;
  const collectionId = pathParams.collectionId;
  const page =
    flowType === SelectPlayerFlowType.BIND_STREAM ? PointCreateModalPages.STREAM : PointCreateModalPages.POINT;

  const {
    addressState,
    isButtonLoading,
    createPointError,
    extendCost,
    needShowAcceptanceAdvancedModal,
    isAcceptanceAdvancedNull,
    isAcceptanceAdvancedPending,
    canAddPointWithPayment,
    isButtonDisabled,
    isShowHintAboutPrice,
    onCreatePointButtonClick,
  } = usePointCreateViewModel({
    flowType,
    isTemplateMode,
    targetStreamId,
    collectionId,
    page,
    notification,
    redirectToPointDrawer: (point: PointInfoTypeFragment) => {
      navigate(RouterUtils.generatePath(POINTS_PAGE_PATH, { id: point!.id }), { replace: true });
    },
    replaceModal,
    openModal,
  });

  useEffect(() => {
    if (needShowAcceptanceAdvancedModal) {
      replaceModal(ModalTypes.ACCEPTANCE_ADVANCED, {});
    }
  }, [needShowAcceptanceAdvancedModal]);

  return (
    <ModalScreen
      title={
        <ModalTitle
          isAcceptanceAdvancedNull={isAcceptanceAdvancedNull}
          isPointPage={page === PointCreateModalPages.POINT}
          canAddPointWithPayment={canAddPointWithPayment}
        />
      }
      titlePlacement="left"
      onClose={close}
      contentClassName={isIpad ? styles.content : undefined}
      actionButtons={[
        <PointCreateButton
          key="button_create_new_point_modal"
          elementId="button_create_new_point_modal"
          isPointPage={page === PointCreateModalPages.POINT}
          onClick={onCreatePointButtonClick}
          isDisabled={isButtonDisabled}
          isLoading={isButtonLoading}
          isAcceptanceAdvancedPending={isAcceptanceAdvancedPending}
          canAddPointWithPayment={canAddPointWithPayment}
          extendCost={extendCost}
        />,
      ]}>
      <PopularTariffWarning isStreamPage={page === PointCreateModalPages.STREAM} />

      <ModalContent
        isAcceptanceAdvancedNull={isAcceptanceAdvancedNull}
        isAcceptanceAdvancedPending={isAcceptanceAdvancedPending}
        addressState={addressState}
        createPointError={createPointError}
        isShowHintAboutPrice={isShowHintAboutPrice}
        openModal={openModal}
      />
    </ModalScreen>
  );
};

export default memo(PointCreateModal);
